import { Destination } from "@/lib/Types/TopListResponse/TopListResponse";
import { channel } from "@/lib/Types/Channel";
import { PATH_LISTE_HOTELS, PATH_LISTE_URLAUB } from "@/lib/StaticData/paths";

export const createItemUrl = (
  currentUrl: string | null,
  currentChannel: string | undefined,
  item: Destination,
  includeRegion = true,
  defaultSort: string
): string => {
  if (currentUrl) {
    const url = new URL(currentUrl);
    url.pathname =
      currentChannel === channel.PACKAGE
        ? PATH_LISTE_URLAUB
        : PATH_LISTE_HOTELS;
    url.searchParams.set("country", item.country.isoCode);
    if (includeRegion) {
      url.searchParams.set("region", item.region.id.toString());
    }
    url.searchParams.set("sort", defaultSort);
    return url.toString();
  }
  return "";
};

export enum ListType {
  COUNTRY = "COUNTRY",
  REGION = "REGION",
}

export const getListType = (searchParams: URLSearchParams): ListType => {
  return searchParams.get("country") ? ListType.REGION : ListType.COUNTRY;
};

/**
 * Converts old travellers and travellerRoomAllocations format to new rooms format
 * @param travellers - Comma-separated string of traveller ages (e.g., "25,25,10,8")
 * @param travellerRoomAllocations - Comma-separated string of room sizes (e.g., "3,1")
 * @returns Rooms string in new format (e.g., "25,25,10;8") or null if conversion fails
 */
export const convertTravellersToRooms = (
  travellers: string,
  travellerRoomAllocations: string
): string | null => {
  if (!travellers || !travellerRoomAllocations) {
    return null;
  }

  const travellersArray = travellers.split(",").map((age) => parseInt(age, 10));
  const roomAllocations = travellerRoomAllocations
    .split(",")
    .map((count) => parseInt(count, 10));

  const adults = travellersArray.filter((age) => age >= 25);
  const children = travellersArray.filter((age) => age < 25);
  const totalRooms = roomAllocations.length;

  const rooms = [];
  let adultIndex = 0;
  let childIndex = 0;

  for (let i = 0; i < totalRooms; i++) {
    const roomSize = roomAllocations[i];
    const room = [];

    // Calculate adults for this room
    const adultsForThisRoom = Math.min(
      Math.ceil((adults.length - adultIndex) / (totalRooms - i)),
      roomSize
    );

    // Add adults to the room
    for (let j = 0; j < adultsForThisRoom; j++) {
      if (adultIndex < adults.length) {
        room.push(adults[adultIndex]);
        adultIndex++;
      }
    }

    // Calculate children for this room
    const remainingSpace = roomSize - adultsForThisRoom;
    const childrenForThisRoom = Math.min(
      Math.ceil((children.length - childIndex) / (totalRooms - i)),
      remainingSpace
    );

    // Add children to the room
    for (let j = 0; j < childrenForThisRoom; j++) {
      if (childIndex < children.length) {
        room.push(children[childIndex]);
        childIndex++;
      }
    }

    rooms.push(room.join(","));
  }

  return rooms.join(";");
};

export const getTravellersFromRooms = (rooms: string): string[] => {
  return rooms
    .split(";")
    .flatMap((room) => room.split(","))
    .filter((age) => age.trim() !== "");
};

export const getRoomAllocationsFromRooms = (rooms: string): number[] => {
  return rooms
    .split(";")
    .map((room) => room.split(",").length)
    .filter((count) => count > 0);
};

export const buildLandingPageUrl = (args: {
  requestUrl: string | null;
  domain: string;
  pathname: string | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchParams: Record<string, any> | undefined;
}): string => {
  const { requestUrl, domain, pathname, searchParams } = args;

  const paramsString = (() => {
    const sp = new URLSearchParams();
    Object.entries(searchParams || {}).forEach(([key, value]) => {
      sp.set(key, String(value));
    });
    return sp.toString();
  })();

  try {
    if (requestUrl) {
      const url = new URL(requestUrl);
      if (pathname) url.pathname = pathname;
      url.search = paramsString;
      return url.href;
    }
  } catch {}

  const protocol =
    domain.includes("localhost") || domain.startsWith("local.")
      ? "http"
      : "https";
  const path = pathname || "";
  return `${protocol}://${domain}${path}${
    paramsString ? `?${paramsString}` : ""
  }`;
};
